import {
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  Output,
  TemplateRef
} from '@angular/core';
import { DOCUMENT, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { IonicModule, ItemReorderCustomEvent } from '@ionic/angular';

export interface ReorderableItem {
  id: number | string;
}

/**
 * Generic drag-to-reorder list, reusable for any list of `{ id }` items.
 * Owns only the Ionic reorder mechanics; the consumer projects the whole row
 * (markup and styling) via an `<ng-template let-item let-reorderEnabled="reorderEnabled">`
 * — placing its own `<ion-reorder>` handle — and persists the new order from `reordered`.
 */
@Component({
  selector: 'fl-reorderable-list',
  standalone: true,
  imports: [IonicModule, NgFor, NgIf, NgTemplateOutlet],
  templateUrl: './reorderable-list.component.html'
})
export class ReorderableListComponent<T extends ReorderableItem> {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  @Input() items: T[] = [];
  /** When false the list is read-only and projected rows hide their drag handle. */
  @Input() reorderEnabled = false;
  @Input() layout: 'list' | 'grid' = 'list';

  draggingIndex: number | null = null;
  dropIndex: number | null = null;

  @ContentChild(TemplateRef) itemTemplate?: TemplateRef<{
    $implicit: T;
    reorderEnabled: boolean;
  }>;

  /** Emits the full id order after each drag (typed to the item's id). */
  @Output() reordered = new EventEmitter<Array<T['id']>>();

  onPointerDown(event: PointerEvent, index: number): void {
    if (!this.reorderEnabled) {
      return;
    }
    const handle = (event.target as HTMLElement)?.closest(
      '[data-reorder-handle]'
    );
    if (!handle) {
      return;
    }
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.draggingIndex = index;
    this.dropIndex = index;
  }

  onPointerMove(event: PointerEvent): void {
    if (this.draggingIndex === null) {
      return;
    }
    event.preventDefault();
    const under = this.document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest('[data-reorder-index]');
    if (!under) {
      return;
    }
    const index = Number(under.getAttribute('data-reorder-index'));
    if (!Number.isNaN(index)) {
      this.dropIndex = index;
    }
  }

  onPointerUp(): void {
    const from = this.draggingIndex;
    const to = this.dropIndex;
    this.draggingIndex = null;
    this.dropIndex = null;
    if (from === null || to === null || from === to) {
      return;
    }
    const items = [...this.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    this.items = items;
    this.reordered.emit(items.map(item => item.id));
  }

  onReorder(event: ItemReorderCustomEvent): void {
    const items = [...this.items];
    const [moved] = items.splice(event.detail.from, 1);
    items.splice(event.detail.to, 0, moved);
    event.detail.complete();
    this.items = items;
    this.reordered.emit(items.map(item => item.id));
  }
}
